import React from 'react';
import { RotateCcwIcon } from 'lucide-react';
import { BeheerLayout } from '../../components/beheer/BeheerLayout';
import { BeheerSection } from '../../components/beheer/BeheerSection';
import { AgentRoster } from '../../components/beheer/agents/AgentRoster';
import { PermissionMatrix } from '../../components/beheer/agents/PermissionMatrix';
import { PERMISSION_META } from '../../data/beheer/permissionMeta';
import { capabilities, defaultPermissions } from '../../data/beheer/permissions';
import { actionLevels } from '../../data/levels';
import { agents } from '../../data/agents';
import { getAgent } from '../../utils/agents';
import { db } from '../../utils/db';
import { useDb } from '../../hooks/useDb';
import type { AgentId } from '../../types/anna';
import type { CapabilityId, Permission } from '../../types/settings';

/** Who works for you, on what, and what each agent may do by itself. Every change lands in the shared db and the log. */
export function Agents() {
  const matrix = useDb(t => t.settings.permissions);
  const paused = useDb(t => t.meta.pausedAgents);
  const changed = JSON.stringify(matrix) !== JSON.stringify(defaultPermissions);
  const change = (agent: AgentId, cap: CapabilityId, next: Permission) => {
    const c = capabilities.find(x => x.id === cap);
    db.updateSettings({
      permissions: {
        ...matrix,
        [agent]: {
          ...matrix[agent],
          [cap]: next
        }
      }
    }, `Recht ${getAgent(agent).name} · ${c?.label.toLowerCase()} → ${PERMISSION_META[next].short.toLowerCase()}`);
  };
  return <BeheerLayout title="Agents" intro="Wie werkt er voor je, waaraan, en wat mag elke agent zelf. Elke wijziging komt in het logboek." aside={<p className="text-[14px] text-ink-soft" data-id="element-1812" data-mp="3ih3fe03ztkj4">
          {agents.length - paused.length} van {agents.length} actief
        </p>} data-id="element-1811" data-mp="38qinswmqlg9p">
      <BeheerSection id="roster" title="Nu" description="Status, model en cloudkosten per agent. Een gepauzeerde agent houdt zijn taak, maar komt niet verder." className="mt-10" data-id="element-1813" data-mp="3nf51ktwptzyt">
        <AgentRoster paused={paused} onPause={(agent, value) => db.setAgentPaused(agent, value)} data-id="element-1814" data-mp="32soztjt6pbct" />
      </BeheerSection>

      <BeheerSection id="rechten" title="Rechten" description="Tik op een cel om het niveau te wijzigen. Sloten, betalen en verwijderen staan vast op Nooit." className="mt-16" action={changed && <button type="button" onClick={() => db.updateSettings({
      permissions: structuredClone(defaultPermissions)
    }, 'Rechten teruggezet naar standaard')} className="inline-flex h-9 items-center gap-1.5 whitespace-nowrap rounded-full px-3 text-[13px] text-ink-muted transition-colors duration-150 hover:bg-white/[0.06] hover:text-ink" data-id="element-1816" data-mp="3lhn77jdi75pj">
              <RotateCcwIcon className="h-3.5 w-3.5" aria-hidden data-id="element-1817" data-mp="213uvznxvqvt8" /> Standaard herstellen
            </button>} data-id="element-1815" data-mp="2xr9avgnvykp5">
        <PermissionMatrix matrix={matrix} onChange={change} data-id="element-1818" data-mp="1w5kzna0f5p61" />
        <dl className="mt-6 grid gap-x-8 gap-y-3 text-[13px] sm:grid-cols-2 xl:grid-cols-4" data-id="element-1819" data-mp="blfyaav1el3y">
          {actionLevels.map(l => <div key={l.id} data-id="element-1820" data-mp="gjzio6tzfou7">
              <dt className="font-medium text-ink-soft" data-id="element-1821" data-mp="1vwacqsgw128">{l.label}</dt>
              <dd className="mt-0.5 leading-relaxed text-ink-muted" data-id="element-1822" data-mp="1vmlj6j5rzy4">{l.rule}</dd>
            </div>)}
        </dl>
      </BeheerSection>
    </BeheerLayout>;
}